import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ITransitionProps } from '@/features/routing';
import {
  registerViewTransitionCallbacks,
  unregisterViewTransitionCallbacks,
  useLocation,
} from '@/features/routing';
import { SECTIONS } from '../../features/routing/constants';
import { parsePathname } from '../../features/routing/utils';
import { useHomePagePreference } from '../../lib/useHomePagePreference';
import { clx } from '../../lib/utils';
import { AppLink } from '../AppLink';
import { NavHomeIcon } from '../icons/NavHomeIcon';
import { NavLogIcon } from '../icons/NavLogIcon';
import { NavPatchesIcon } from '../icons/NavPatchesIcon';
import { NavProfileIcon } from '../icons/NavProfileIcon';
import './BottomNavigation.styles.css';

// const hiddenOnPages = ['/asdf'];
const hiddenOnPages = ['/', '/login', '/register'];

const MOOD_LOG_ENTRY_HREF = '/mood-log-entry';

const getBottomNavigationItems = (homePagePreference: string) => {
  return [
    { href: `/home/${homePagePreference}`, label: 'Home', icon: NavHomeIcon },
    { href: '/patches', label: 'Patches', icon: NavPatchesIcon },
    { href: MOOD_LOG_ENTRY_HREF, label: 'Log', icon: NavLogIcon },
    { href: '/profile', label: 'Profile', icon: NavProfileIcon },
  ];
};
export const BottomNavigation = ({ className }: { className?: string }) => {
  const pathname = useLocation();
  const isHidden = hiddenOnPages.includes(pathname);
  const [homePagePreference] = useHomePagePreference();
  const [clickedLabel, setClickedLabel] = useState<string | undefined>(undefined);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>, label: string): false => {
      event.preventDefault();
      setClickedLabel(label);
      return false;
    },
    []
  );

  const bottomNavigationItems = useMemo(() => {
    return getBottomNavigationItems(homePagePreference);
  }, [homePagePreference]);

  const activeNavItemLabel = useMemo(() => {
    const parsedPathname = parsePathname(pathname);
    setClickedLabel(undefined); // Reset the clicked label.
    switch (parsedPathname.section) {
      case SECTIONS.Home:
        return 'Home';
      case SECTIONS.Patches:
        return 'Patches';
      case SECTIONS.Mood_log_entry:
        return 'Log';
      case SECTIONS.Profile:
        return 'Profile';
      default:
        return undefined;
    }
  }, [pathname]);

  const bottomNavigationRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    registerViewTransitionCallbacks('bottom-navigation', {
      onBeforeTransition: ({ currentPathname, nextPathname }: ITransitionProps) => {
        const willHide = hiddenOnPages.includes(nextPathname);
        const isHidden = hiddenOnPages.includes(currentPathname);
        const vtName =
          willHide === isHidden ? 'bottom-navigation-static' : 'bottom-navigation-active';
        bottomNavigationRef.current?.removeAttribute('data-transition');
        bottomNavigationRef.current?.setAttribute('data-transition', vtName);
      },
    });

    return () => {
      unregisterViewTransitionCallbacks('bottom-navigation');
    };
  }, []);

  return (
    <div
      className={clx(
        'bottom-navigation bg-background-secondary relative w-screen shrink-0',
        className,
        isHidden && 'hidden'
      )}
      ref={bottomNavigationRef}
    >
      <div className="h-19 mx-auto flex max-w-xs items-center justify-around text-xs">
        {bottomNavigationItems.map((item) => (
          <AppLink
            href={item.href}
            key={item.href}
            onClick={(event) => handleClick(event, item.label)}
            className={clx(
              'size-15 flex flex-col items-center justify-center rounded-full',
              'transition-all duration-1000',
              (activeNavItemLabel === item.label || clickedLabel === item.label) &&
                'bg-background-primary',
              clickedLabel === item.label && 'bg-background-tertiary/80 duration-200'
            )}
          >
            <div className="h-7.5">
              <item.icon />
            </div>
            {item.label}
          </AppLink>
        ))}
      </div>
    </div>
  );
};
